import {
  Box,
  Button,
  CircularProgress,
  Fade,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/cs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import axios from "axios";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { load as loadCaptcha } from "recaptcha-v3";
import dynamic from "next/dynamic";
import { useForm } from "react-hook-form";

dayjs.locale("cs");

const DialogDynamic = dynamic(() => import("./reservation-dialog"), {
  loading: () => <CircularProgress />,
});

interface IReservationTimes {
  times: string[];
  date: string;
}

const fetchReservationTimes = (date: string) => async () => {
  const response = await fetch(
    `https://api.pivovarcaslav.cz/booking/availableHours.php?date=${date}`
  );
  return (await response.json()) as IReservationTimes;
};

export interface IReservationForm {
  date: Dayjs;
  time: string;

  //
  name: string;
  email: string;
  phone: string;
  message: string;
  occasion?: string;
  peopleCount?: number;
}

interface IReservationPostForm {
  date: string;
  time: string;

  name: string;
  email: string;
  phone: string;
  message: string;
  occasion?: string;
  peopleCount?: number;
}

const Calendar: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,

    formState: { errors },
  } = useForm<IReservationForm>({
    defaultValues: {
      date: dayjs(),
      peopleCount: 2,
    },
    reValidateMode: "onBlur",
    mode: "onBlur",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialOpen, setInitialOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<IReservationTimes>(
    "reservationTimes",
    fetchReservationTimes(
      watch("date")?.format("YYYY-MM-DD") ?? dayjs().format("YYYY-MM-DD")
    ),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const { mutateAsync, isLoading: isMutating } = useMutation({
    mutationFn: async (
      data: IReservationPostForm & { "g-recaptcha-response": string }
    ) => {
      const a = await axios.post<Record<string, never>>(
        "https://api.pivovarcaslav.cz/booking/create.php",
        data
      );

      return a;
    },
  });

  const handleInvalid = () => {
    enqueueSnackbar("Prosím, zkontrolujte své údaje", {
      variant: "error",
    });
  };

  const submit = async (data: IReservationForm) => {
    const { date, ...rest } = data;

    try {
      const captcha = await loadCaptcha(process.env.NEXT_PUBLIC_CAPTCHA, {
        useRecaptchaNet: true,
        autoHideBadge: true,
      });
      const token = await captcha.execute("reservation");

      const result = await mutateAsync({
        date: data.date.format("YYYY-MM-DD"),
        "g-recaptcha-response": token,
        ...rest,
      });

      if (result.status === 200) {
        enqueueSnackbar("Rezervace byla úspěšně odeslána", {
          variant: "success",
        });
        reset();
        setDialogOpen(false);
      }
    } catch (e) {
      console.log(e);
      enqueueSnackbar("Něco se pokazilo, zkuste to prosím později", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    // remove time from form
    setValue("time", "");
    refetch();
  }, [refetch, setValue, watch("date")]);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
          },
        })}
      >
        <Box
          sx={(theme) => ({
            width: "100%",
            [theme.breakpoints.down("sm")]: {
              flexDirection: "row",
              display: "flex",
            },
          })}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <Box
              sx={(theme) => ({
                [theme.breakpoints.down("md")]: { display: "none" },
              })}
            >
              <StaticDatePicker
                {...(isDesktop && { displayStaticWrapperAs: "desktop" })}
                openTo="day"
                minDate={dayjs()}
                maxDate={dayjs().add(1, "month")}
                value={watch("date")}
                onChange={(newValue) => {
                  setValue("date", newValue);
                }}
                views={["day"]}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
            <Box
              sx={(theme) => ({
                [theme.breakpoints.up("md")]: { display: "none" },
                width: "100%",
                padding: 2,
              })}
            >
              <DatePicker
                label="Datum pro rezervaci"
                minDate={dayjs()}
                maxDate={dayjs().add(1, "month")}
                value={watch("date")}
                onChange={(newValue) => {
                  setValue("date", newValue);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Box>
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ padding: 2 }}>Vyberte termín</Typography>

          <Box
            sx={{ width: "100%", maxHeight: 240, overflowY: "auto" }}
            className="slimScroll"
          >
            {isLoading && (
              <Box>
                <CircularProgress />
              </Box>
            )}
            {!isLoading && data.times.length === 0 && (
              <Box sx={{ alignSelf: "center", textAlign: "center" }}>
                <Typography variant="body1">
                  K vybrané datu nejsou žádné dostupné termíny
                </Typography>
              </Box>
            )}
            {!isLoading && data && (
              <>
                <List sx={{ overflowY: "auto", height: "100%" }}>
                  {data.times.map((day, index) => {
                    return (
                      <ListItem key={index} disablePadding>
                        <ListItemButton
                          selected={watch("time") === day}
                          onClick={() => {
                            setValue("time", day);
                          }}
                        >
                          {day}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}
          </Box>
          <Fade in={!!watch("date") && !!watch("time")}>
            <Button
              sx={(theme) => ({ m: theme.spacing(2) })}
              onClick={() => {
                if (!initialOpen) {
                  setInitialOpen(true);
                }
                setDialogOpen(true);
              }}
            >
              Pokračovat
            </Button>
          </Fade>
        </Box>
      </Box>
      {initialOpen && (
        <DialogDynamic
          handleSubmit={handleSubmit(submit, handleInvalid)}
          onClose={() => setDialogOpen(false)}
          dialogOpen={dialogOpen}
          errors={errors}
          isMutating={isMutating}
          register={register}
          watch={watch}
        />
      )}
    </form>
  );
};

export default Calendar;
